import adminApiClient from "@/lib/api/admin-api-client";
import type {
  AccessRequestOrigin,
  AccessRequestStatus,
  AccessScope,
  ApiPlatform,
  KeyEnvironment,
  RequesterType,
} from "@/lib/api/access-requests";
import type { ExpectedDailyVolume } from "@/lib/api/login-user";

export type AdminActor = {
  id: string;
  name: string;
};

export type AdminAccessRequest = {
  id: string;
  portal_user_id: string;
  /** Display name for the integrator (AES-275+). */
  integrator_name: string;
  api_platform: ApiPlatform;
  requester_type: RequesterType;
  intended_use_case: string;
  /** Present when Task 5+ persists environment; omitted on legacy requests. */
  environment?: KeyEnvironment | null;
  /**
   * Present when AES-359+ returns import metadata (AES-361 admin). Optional on
   * legacy rows.
   */
  origin?: AccessRequestOrigin | null;
  /**
   * Existing Glofox integrator id when imported or credentials already issued
   * (AES-359+ / AES-361). Optional on legacy rows.
   */
  integrator_id?: string | null;
  access_scope?: AccessScope | null;
  location_ids: string[];
  /** Live integrator location names when access_scope is "locations". */
  location_names: string[];
  namespaces: string[];
  /** Null on some integrator requests (AES-275+). */
  expected_daily_volume: ExpectedDailyVolume | null;
  integrations: string[];
  status: AccessRequestStatus;
  contact_name: string;
  email: string;
  company_name: string;
  reviewed_by?: string | null;
  reviewed_by_name?: string | null;
  reviewed_at?: string | null;
  /** Applicant-visible reason when status is rejected. */
  rejection_reason?: string | null;
  /** Applicant response to the previous rejection. */
  contestation_message?: string | null;
  /** Time when the applicant contested the rejection. */
  contested_at?: string | null;
  /** True only for a rejected request that has not been contested. */
  can_contest?: boolean;
  /** Present on admin detail / upsert responses; omitted from list. */
  internal_note?: string | null;
  internal_note_created_by?: AdminActor | null;
  internal_note_created_at?: string | null;
  internal_note_updated_by?: AdminActor | null;
  internal_note_updated_at?: string | null;
  /** Sandbox test locations on the applicant account (detail GET). */
  test_locations?: AdminTestLocation[];
  /** Link sync status for an imported request (AES-361). */
  sync_status?: "pending" | "linked" | "failed" | null;
  /** Last Glofox import attempt for this link (AES-361). */
  last_synced_at?: string | null;
  /**
   * Resolved Glofox studios for a live request. Present on admin detail when
   * api-portal-live-credentials is on.
   */
  live_scope?: LiveScope | null;
  /**
   * Live fulfillment when the portal created a Glofox integrator (AES-389).
   * Omitted for sandbox and unprovisioned live rows.
   */
  fulfillment?: "provisioned" | "revoked" | null;
  /**
   * Live API key state. Revoke deletes the key when this is pending, issued,
   * or failed.
   */
  credentials_status?: "none" | "pending" | "issued" | "failed" | null;
  created_at: string;
  updated_at: string;
};

export type LiveScopeMatchStatus = "resolved" | "not_found" | "ambiguous";

export type LiveScopeLocation = {
  status: LiveScopeMatchStatus;
  requested_name?: string;
  branch_id?: string;
  name?: string;
  namespace?: string;
  active?: boolean;
  demo?: boolean;
};

export type LiveScopeNamespace = {
  namespace: string;
  status: LiveScopeMatchStatus;
  locations: LiveScopeLocation[];
};

export type LiveScope = {
  access_scope: AccessScope;
  locations: LiveScopeLocation[];
  namespaces: LiveScopeNamespace[];
};

export type AdminTestLocationStatus = "creating" | "ready" | "failed";

export type AdminTestLocation = {
  id: string;
  display_name: string;
  status: AdminTestLocationStatus;
  branch_id?: string | null;
  namespace?: string | null;
  admin_email?: string | null;
  failure_reason?: string | null;
  access_request_id: string;
  sandbox_workspace_id: string;
  integrator_id?: string | null;
  integrator_name: string;
  on_this_request: boolean;
};

export type AdminAccessRequestSummary = {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  /** Pending requests with contested_at set (AES-365). Optional until backend ships. */
  contested?: number;
};

export type AdminListAccessRequestsResponse = {
  requests: AdminAccessRequest[];
  total: number;
  summary: AdminAccessRequestSummary;
};

export type AdminListAccessRequestsParams = {
  status?: AccessRequestStatus;
  /** When true, list only pending requests that have been contested. */
  contested?: boolean;
  requester_type?: RequesterType;
  q?: string;
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
};

const ADMIN_ACCESS_REQUESTS_PATH = "/api/v1/admin/access-requests";

export async function listAdminAccessRequests(
  params: AdminListAccessRequestsParams = {},
): Promise<AdminListAccessRequestsResponse> {
  const { data } = await adminApiClient.get<AdminListAccessRequestsResponse>(
    ADMIN_ACCESS_REQUESTS_PATH,
    {
      params: {
        ...(params.status ? { status: params.status } : {}),
        ...(params.contested ? { contested: true } : {}),
        ...(params.requester_type
          ? { requester_type: params.requester_type }
          : {}),
        ...(params.q?.trim() ? { q: params.q.trim() } : {}),
        ...(params.page ? { page: params.page } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.sort ? { sort: params.sort } : {}),
        ...(params.order ? { order: params.order } : {}),
      },
    },
  );
  return data;
}

export async function getAdminAccessRequest(
  id: string,
): Promise<AdminAccessRequest> {
  const { data } = await adminApiClient.get<AdminAccessRequest>(
    `${ADMIN_ACCESS_REQUESTS_PATH}/${id}`,
  );
  return data;
}

export async function upsertAdminAccessRequestInternalNote(
  id: string,
  internalNote: string,
): Promise<AdminAccessRequest> {
  const { data } = await adminApiClient.put<AdminAccessRequest>(
    `${ADMIN_ACCESS_REQUESTS_PATH}/${id}/internal-note`,
    { internal_note: internalNote },
  );
  return data;
}

export type AdminReviewStatus = Extract<
  AccessRequestStatus,
  "approved" | "rejected"
>;

export async function reviewAdminAccessRequest(
  id: string,
  status: AdminReviewStatus,
  rejectionReason?: string,
  confirmation?: string,
): Promise<AdminAccessRequest> {
  const { data } = await adminApiClient.post<AdminAccessRequest>(
    `${ADMIN_ACCESS_REQUESTS_PATH}/${id}/review`,
    {
      status,
      ...(status === "rejected" && rejectionReason
        ? { rejection_reason: rejectionReason }
        : {}),
      ...(status === "approved" && confirmation
        ? { confirmation }
        : {}),
    },
  );
  return data;
}

export async function revokeAdminLiveAccess(
  id: string,
  confirmation: string,
): Promise<AdminAccessRequest> {
  const { data } = await adminApiClient.post<AdminAccessRequest>(
    `${ADMIN_ACCESS_REQUESTS_PATH}/${id}/revoke`,
    { confirmation },
  );
  return data;
}
